import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RefreshCw, Loader2, Globe, Edit2, Check, X, MessageSquare, ChevronDown, ChevronUp, CheckCircle, XCircle, Search, Package, ArrowRight } from "lucide-react";
import axios, { getApiUrl } from "@/lib/api";

const API = `${getApiUrl()}/api`;

const GROUP_COLORS = {
  Xbox: "#107C10",
  PlayStation: "#003087",
  Nintendo: "#E60012",
  PC: "#1B2838",
  Mobile: "#34A853",
  Store: "#7C3AED",
  Other: "#6B7280",
};

const GROUP_ORDER = ["Xbox", "PlayStation", "Nintendo", "PC", "Mobile", "Store", "Other"];

export default function PlatformCoverageDialog({ open, onOpenChange, onAddGame }) {
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [editingPlatform, setEditingPlatform] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [requestCounts, setRequestCounts] = useState({});
  const [requests, setRequests] = useState([]);
  const [expandedPlatform, setExpandedPlatform] = useState(null);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [scanningSystem, setScanningSystem] = useState(null);
  const [scanResults, setScanResults] = useState({});  // {platformName: {updates: [], scanned: 0, ...}}

  const fetchCoverage = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/master-db/platform-coverage`);
      setPlatforms(data.platforms || []);
    } catch (err) {
      console.error("Failed to fetch platform coverage:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchRequestCounts = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/game-requests/counts`);
      setRequestCounts(data.counts || {});
    } catch (err) {
      console.error("Failed to fetch request counts:", err);
    }
  }, []);

  const fetchRequests = useCallback(async () => {
    setRequestsLoading(true);
    try {
      const { data } = await axios.get(`${API}/game-requests?status=pending`);
      setRequests(data.requests || []);
    } catch (err) {
      console.error("Failed to fetch requests:", err);
    } finally {
      setRequestsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchCoverage();
      fetchRequestCounts();
      fetchRequests();
    }
  }, [open, fetchCoverage, fetchRequestCounts, fetchRequests]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const { data } = await axios.post(`${API}/master-db/platform-coverage/refresh`);
      setPlatforms(data.platforms || []);
    } catch (err) {
      console.error("Failed to refresh:", err);
    } finally {
      setRefreshing(false);
    }
  };

  const handleSaveManual = async (platformName) => {
    const val = parseInt(editValue, 10);
    if (isNaN(val) || val < 0) return;
    try {
      await axios.put(`${API}/master-db/platform-coverage/${encodeURIComponent(platformName)}/manual-count`, {
        manualCount: val,
      });
      setPlatforms((prev) =>
        prev.map((p) => (p.name === platformName ? { ...p, manualCount: val } : p))
      );
      setEditingPlatform(null);
    } catch (err) {
      console.error("Failed to save manual count:", err);
    }
  };

  const handleRequestAction = async (requestId, status) => {
    try {
      await axios.put(`${API}/game-requests/${requestId}/status`, { status });
      setRequests((prev) => prev.filter((r) => r.id !== requestId));
      setRequestCounts((prev) => {
        const req = requests.find((r) => r.id === requestId);
        if (!req) return prev;
        const newCounts = { ...prev };
        if (newCounts[req.platform]) {
          newCounts[req.platform] = Math.max(0, newCounts[req.platform] - 1);
          if (newCounts[req.platform] === 0) delete newCounts[req.platform];
        }
        return newCounts;
      });
    } catch (err) {
      console.error("Failed to update request:", err);
    }
  };

  const togglePlatformRequests = (platformName) => {
    setExpandedPlatform(expandedPlatform === platformName ? null : platformName);
  };

  const handleScanSystem = async (platformName) => {
    setScanningSystem(platformName);
    try {
      const { data } = await axios.post(`${API}/master-db/platform-coverage/${encodeURIComponent(platformName)}/scan-updates`);
      setScanResults((prev) => ({ ...prev, [platformName]: data }));
    } catch (err) {
      console.error("Scan failed:", err);
    } finally {
      setScanningSystem(null);
    }
  };

  // Map platform names used in requests to groups used in the coverage dialog
  const getRequestsForGroup = (groupName) => {
    const platformMap = {
      Xbox: ["Xbox"],
      PlayStation: ["PlayStation"],
      Nintendo: ["Nintendo"],
      PC: ["Steam", "Epic", "GOG"],
      Mobile: ["Mobile"],
      Store: ["Steam", "Epic", "GOG"],
      Other: ["RetroAchievements", "Other"],
    };
    const matchPlatforms = platformMap[groupName] || [];
    return requests.filter((r) => matchPlatforms.includes(r.platform));
  };

  const getRequestCountForGroup = (groupName) => {
    const platformMap = {
      Xbox: ["Xbox"],
      PlayStation: ["PlayStation"],
      Nintendo: ["Nintendo"],
      PC: ["Steam", "Epic", "GOG"],
      Mobile: ["Mobile"],
      Store: ["Steam", "Epic", "GOG"],
      Other: ["RetroAchievements", "Other"],
    };
    const matchPlatforms = platformMap[groupName] || [];
    return matchPlatforms.reduce((sum, p) => sum + (requestCounts[p] || 0), 0);
  };

  const grouped = GROUP_ORDER.map((group) => ({
    group,
    color: GROUP_COLORS[group],
    platforms: platforms.filter((p) => p.group === group),
    requestCount: getRequestCountForGroup(group),
    requests: getRequestsForGroup(group),
  })).filter((g) => g.platforms.length > 0);

  const totalIgdb = platforms.reduce((s, p) => s + (p.igdbCount || p.manualCount || 0), 0);
  const totalMdb = platforms.reduce((s, p) => s + (p.mdbCount || 0), 0);
  const totalRequests = Object.values(requestCounts).reduce((s, c) => s + c, 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] bg-[#0A0A0A] border-white/10 text-white overflow-hidden flex flex-col" data-testid="platform-coverage-dialog">
        <DialogHeader>
          <DialogTitle className="text-lg font-heading flex items-center gap-2">
            <Globe className="w-5 h-5 text-cyan-400" />
            Platform Coverage Tracker
            {totalRequests > 0 && (
              <span className="ml-2 px-2 py-0.5 rounded-full bg-orange-500/20 text-orange-400 text-xs font-mono">
                {totalRequests} request{totalRequests !== 1 ? "s" : ""}
              </span>
            )}
          </DialogTitle>
          <DialogDescription className="text-white/40 text-xs">
            Total games available per platform (via IGDB) vs. games entered in MDB.
            {platforms.some((p) => p.lastRefreshed) && (
              <span className="ml-2 text-white/20">
                Last refreshed: {new Date(platforms.find((p) => p.lastRefreshed)?.lastRefreshed).toLocaleDateString()}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        {/* Refresh button + summary */}
        <div className="flex items-center justify-between py-2 border-b border-white/5">
          <div className="flex items-center gap-4 text-xs text-white/50">
            <span>
              Total tracked: <span className="text-cyan-400 font-mono">{totalIgdb.toLocaleString()}</span>
            </span>
            <span>
              In MDB: <span className="text-green-400 font-mono">{totalMdb.toLocaleString()}</span>
            </span>
            <span>
              Remaining: <span className="text-yellow-400 font-mono">{(totalIgdb - totalMdb).toLocaleString()}</span>
            </span>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10"
            onClick={handleRefresh}
            disabled={refreshing}
            data-testid="refresh-igdb-btn"
          >
            {refreshing ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
            {refreshing ? "Fetching from IGDB..." : "Refresh from IGDB"}
          </Button>
        </div>

        {/* Platform table */}
        <div className="flex-1 overflow-y-auto pr-1 -mr-1">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-white/30" />
            </div>
          ) : (
            <div className="space-y-4 py-2">
              {grouped.map(({ group, color, platforms: gPlats, requestCount, requests: groupRequests }) => (
                <div key={group} data-testid={`coverage-group-${group.toLowerCase()}`}>
                  {/* Group header */}
                  <div className="flex items-center gap-2 mb-1.5">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
                    <span className="text-xs font-semibold uppercase tracking-wider text-white/60">{group}</span>
                    {requestCount > 0 && (
                      <button
                        className="flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-orange-500/15 hover:bg-orange-500/25 transition-colors"
                        onClick={() => togglePlatformRequests(group)}
                        data-testid={`group-requests-badge-${group.toLowerCase()}`}
                      >
                        <MessageSquare className="w-3 h-3 text-orange-400" />
                        <span className="text-[10px] text-orange-400 font-mono">{requestCount}</span>
                        {expandedPlatform === group ? (
                          <ChevronUp className="w-3 h-3 text-orange-400/60" />
                        ) : (
                          <ChevronDown className="w-3 h-3 text-orange-400/60" />
                        )}
                      </button>
                    )}
                    <div className="h-px flex-1 bg-white/5" />
                    <span className="text-[10px] text-white/30 font-mono">
                      {gPlats.reduce((s, p) => s + (p.mdbCount || 0), 0).toLocaleString()} in MDB
                    </span>
                  </div>

                  {/* Expanded requests for this group */}
                  {expandedPlatform === group && groupRequests.length > 0 && (
                    <div className="mb-2 ml-4 space-y-1 border-l-2 border-orange-500/20 pl-3" data-testid={`group-requests-${group.toLowerCase()}`}>
                      <p className="text-[10px] text-orange-400/60 uppercase tracking-wider mb-1">Requested Games</p>
                      {groupRequests.map((req) => (
                        <div key={req.id} className="flex items-center gap-2 py-1.5 px-2 rounded bg-orange-500/5 border border-orange-500/10" data-testid={`request-row-${req.id}`}>
                          <div className="flex-1 min-w-0">
                            <p className="text-white text-xs font-medium truncate">{req.gameName}</p>
                            <p className="text-white/30 text-[10px] truncate">
                              {req.platform}{req.system ? ` · ${req.system}` : ""}
                              {req.notes ? ` · "${req.notes}"` : ""}
                              <span className="text-white/15 ml-1">
                                by {req.requestedByName} · {req.voteCount > 1 ? `${req.voteCount} votes` : "1 vote"}
                              </span>
                            </p>
                          </div>
                          {onAddGame && (
                            <button
                              onClick={() => onAddGame(req)}
                              className="px-2 py-1 rounded text-[10px] font-bold tracking-wider bg-blue-500/15 hover:bg-blue-500/30 text-blue-300 border border-blue-500/30 transition-colors"
                              title="Add this game to the Master Database"
                              data-testid={`request-add-${req.id}`}
                            >
                              ADD
                            </button>
                          )}
                          <button
                            onClick={() => handleRequestAction(req.id, "added")}
                            className="p-1 rounded hover:bg-green-500/20 text-green-400 transition-colors"
                            title="Mark as added"
                            data-testid={`request-approve-${req.id}`}
                          >
                            <CheckCircle className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleRequestAction(req.id, "dismissed")}
                            className="p-1 rounded hover:bg-red-500/20 text-red-400/60 transition-colors"
                            title="Dismiss"
                            data-testid={`request-dismiss-${req.id}`}
                          >
                            <XCircle className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Platform rows */}
                  <div className="space-y-0.5">
                    {gPlats.map((plat) => {
                      const total = plat.igdbCount ?? plat.manualCount ?? null;
                      const pct = total && total > 0 ? Math.min(100, Math.round((plat.mdbCount / total) * 100)) : null;
                      const isEditing = editingPlatform === plat.name;

                      return (
                        <React.Fragment key={plat.name}>
                        <div
                          className="flex items-center gap-3 py-1.5 px-2 rounded hover:bg-white/[0.03] group"
                          data-testid={`coverage-row-${plat.name.replace(/[^a-zA-Z0-9]/g, "-").toLowerCase()}`}
                        >
                          {/* Name */}
                          <span className="text-white/70 text-xs w-36 shrink-0 truncate">{plat.name}</span>

                          {/* Scan button (for platforms with IGDB IDs and MDB games) */}
                          {plat.igdbId && plat.mdbCount > 0 && (
                            <button
                              className={`shrink-0 p-1 rounded transition-colors ${
                                scanningSystem === plat.name
                                  ? "text-cyan-400 animate-pulse"
                                  : scanResults[plat.name]?.updates?.length > 0
                                  ? "text-orange-400 hover:bg-orange-500/10"
                                  : "text-white/20 hover:text-white/40 hover:bg-white/5"
                              }`}
                              onClick={() => handleScanSystem(plat.name)}
                              disabled={scanningSystem !== null}
                              title={scanningSystem === plat.name ? "Scanning..." : "Scan for DLC/title updates"}
                              data-testid={`scan-btn-${plat.name.replace(/[^a-zA-Z0-9]/g, "-").toLowerCase()}`}
                            >
                              {scanningSystem === plat.name ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              ) : (
                                <Search className="w-3.5 h-3.5" />
                              )}
                            </button>
                          )}
                          {/* Scan result badge */}
                          {scanResults[plat.name]?.updates?.length > 0 && (
                            <span className="text-[9px] text-orange-400 font-mono shrink-0">
                              {scanResults[plat.name].updates.filter(u => u.type === "new_dlcs").length} updates
                            </span>
                          )}

                          {/* Progress bar */}
                          <div className="flex-1 h-4 bg-white/5 rounded-full overflow-hidden relative">
                            {pct !== null && (
                              <div
                                className="h-full rounded-full transition-all duration-500"
                                style={{
                                  width: `${Math.max(pct, 1)}%`,
                                  backgroundColor: pct === 100 ? "#10B981" : color,
                                  opacity: 0.7,
                                }}
                              />
                            )}
                            {pct !== null && (
                              <span className="absolute inset-0 flex items-center justify-center text-[9px] text-white/60 font-mono">
                                {pct}%
                              </span>
                            )}
                          </div>

                          {/* MDB count */}
                          <span className="text-green-400/80 font-mono text-[11px] w-14 text-right shrink-0" title="In MDB">
                            {plat.mdbCount.toLocaleString()}
                          </span>

                          {/* Separator */}
                          <span className="text-white/10 text-xs">/</span>

                          {/* Total count (IGDB or manual) */}
                          {isEditing ? (
                            <div className="flex items-center gap-1 w-20 shrink-0">
                              <Input
                                type="number"
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                className="h-5 w-14 text-[11px] bg-white/10 border-white/20 text-white px-1 py-0"
                                autoFocus
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") handleSaveManual(plat.name);
                                  if (e.key === "Escape") setEditingPlatform(null);
                                }}
                                data-testid={`manual-count-input-${plat.name.replace(/[^a-zA-Z0-9]/g, "-").toLowerCase()}`}
                              />
                              <button onClick={() => handleSaveManual(plat.name)} className="text-green-400 hover:text-green-300">
                                <Check className="w-3 h-3" />
                              </button>
                              <button onClick={() => setEditingPlatform(null)} className="text-red-400 hover:text-red-300">
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          ) : (
                            <span
                              className={`font-mono text-[11px] w-20 text-right shrink-0 flex items-center justify-end gap-1 ${total !== null ? "text-cyan-400/80" : "text-white/20"}`}
                              title={total !== null ? (plat.igdbCount != null ? "From IGDB" : "Manual entry") : "Not yet fetched"}
                            >
                              {total !== null ? total.toLocaleString() : "—"}
                              {plat.igdbId === null && (
                                <button
                                  className="opacity-0 group-hover:opacity-100 transition-opacity text-white/30 hover:text-white/60"
                                  onClick={() => {
                                    setEditingPlatform(plat.name);
                                    setEditValue(plat.manualCount?.toString() || "");
                                  }}
                                  title="Edit manual count"
                                >
                                  <Edit2 className="w-2.5 h-2.5" />
                                </button>
                              )}
                            </span>
                          )}
                        </div>
                        {/* Scan results for this platform */}
                        {scanResults[plat.name]?.updates?.length > 0 && (
                          <div className="ml-6 mb-2 space-y-1 border-l-2 border-orange-500/15 pl-3" data-testid={`scan-results-${plat.name.replace(/[^a-zA-Z0-9]/g, "-").toLowerCase()}`}>
                            <p className="text-[10px] text-orange-400/60 uppercase tracking-wider">
                              {scanResults[plat.name].matchesFound} game{scanResults[plat.name].matchesFound !== 1 ? "s" : ""} with DLC updates
                              <span className="text-white/20 ml-2">
                                ({scanResults[plat.name].scanned} scanned · {scanResults[plat.name].igdbGamesWithDlcs} IGDB matches)
                              </span>
                            </p>
                            <div className="max-h-40 overflow-y-auto space-y-0.5">
                              {scanResults[plat.name].updates.filter(u => u.type === "new_dlcs").map((u, i) => (
                                <div key={i} className="flex items-start gap-2 py-1 px-2 rounded bg-orange-500/5 text-xs">
                                  <Package className="w-3 h-3 text-orange-400/60 mt-0.5 shrink-0" />
                                  <div className="min-w-0 flex-1">
                                    <span className="text-white/80 font-medium">{u.gameName}</span>
                                    <span className="text-white/30 ml-1.5">
                                      {u.mdbDlcCount} <ArrowRight className="w-2.5 h-2.5 inline" /> {u.igdbDlcCount} DLCs
                                    </span>
                                    {u.newDlcs?.length > 0 && (
                                      <p className="text-white/30 text-[10px] truncate mt-0.5">
                                        New: {u.newDlcs.join(", ")}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              ))}
                              {scanResults[plat.name].updates.filter(u => u.type === "name_change").map((u, i) => (
                                <div key={`nc-${i}`} className="flex items-center gap-2 py-1 px-2 rounded bg-blue-500/5 text-xs">
                                  <span className="text-blue-400/60 text-[10px] shrink-0">NAME</span>
                                  <span className="text-white/50">"{u.gameName}"</span>
                                  <ArrowRight className="w-3 h-3 text-white/20 shrink-0" />
                                  <span className="text-white/80">"{u.igdbName}"</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {scanResults[plat.name] && scanResults[plat.name].updates?.length === 0 && (
                          <div className="ml-6 mb-1 pl-3">
                            <p className="text-[10px] text-green-400/50">No updates found ({scanResults[plat.name].scanned} games scanned)</p>
                          </div>
                        )}
                      </React.Fragment>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
